import {
  BellIcon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  MailIcon,
  MailOpenIcon,
} from '@hugeicons/core-free-icons';
import { useState } from 'react';
import { HugeIcon } from '../ui/HugeIcon';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import type { Notification } from '../../lib/api';

export function NotificationCenter({
  notifications,
  onOpenNotification,
  onReadAll,
}: {
  notifications: Notification[];
  onOpenNotification: (notification: Notification) => void;
  onReadAll: () => void;
}) {
  const unread = notifications.filter((item) => !item.readAt).length;
  const [open, setOpen] = useState(false);
  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <SheetTrigger
        aria-label={`${unread} unread notifications`}
        className="relative flex size-9 items-center justify-center rounded-full border-0 bg-transparent text-muted-foreground shadow-none outline-none hover:bg-muted hover:text-foreground focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0"
      >
        <HugeIcon icon={BellIcon} size={20} />
        {unread ? (
          <span className="absolute -right-1 -top-1 flex min-w-5 items-center justify-center rounded-full bg-slate-900 px-1 text-[10px] font-bold leading-5 text-white">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </SheetTrigger>
      <SheetContent className="w-[94vw]! max-w-[440px]! bg-background" showCloseButton={false}>
        <SheetHeader className="gap-1 border-b px-5 py-4">
          <div className="flex items-center justify-between gap-3">
            <SheetTitle>Notifications</SheetTitle>
            <div className="flex items-center gap-2" role="group" aria-label="Notification actions">
              <button
                aria-label={unread ? 'Mark all notifications as read' : 'All notifications are read'}
                className="flex size-8 cursor-pointer items-center justify-center rounded-full border-0 bg-slate-100 p-0 text-slate-600 shadow-none outline-none transition-colors hover:bg-slate-200 hover:text-slate-900 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 disabled:cursor-default disabled:opacity-50"
                disabled={!unread}
                onClick={onReadAll}
                title={unread ? 'Mark all read' : 'All notifications are read'}
                type="button"
              >
                <span className="relative flex size-[18px] items-center justify-center">
                  <HugeIcon icon={unread ? MailIcon : MailOpenIcon} size={18} />
                  {unread ? (
                    <span aria-hidden="true" className="absolute -right-1 -top-1 size-2 rounded-full border border-slate-100 bg-amber-500" />
                  ) : null}
                </span>
              </button>
              <SheetClose
                aria-label="Close notifications"
                className="flex size-8 items-center justify-center rounded-full border-0 bg-red-50 text-red-600 shadow-none outline-none transition-colors hover:bg-red-100 hover:text-red-700 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                title="Close"
              >
                <HugeIcon icon={Cancel01Icon} size={16} strokeWidth={2.2} />
              </SheetClose>
            </div>
          </div>
          <SheetDescription className="whitespace-nowrap text-xs leading-5">License, release, approval, and support updates.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-3">
          {notifications.length ? (
            <div className="flex flex-col gap-2">
              {notifications.map((notification) => (
                <button
                  className={`w-full rounded-xl border p-3 text-left transition-colors hover:bg-muted/50 ${notification.readAt ? 'bg-background' : 'bg-muted/30'}`}
                  key={notification.id}
                  onClick={() => {
                    onOpenNotification(notification);
                    setOpen(false);
                  }}
                  type="button"
                >
                  <div className="flex items-start gap-3">
                    <span className={`mt-1 size-2 shrink-0 rounded-full ${notification.readAt ? 'bg-slate-300' : 'bg-blue-600'}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{notification.title}</p>
                      <p className="mt-1 text-xs leading-5 text-slate-600">{notification.body}</p>
                      <p className="mt-2 text-[11px] font-medium text-slate-400">{formatRelative(notification.createdAt)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex h-full min-h-72 flex-col items-center justify-center text-center">
              <HugeIcon className="text-emerald-600" icon={CheckmarkCircle02Icon} size={34} />
              <p className="mt-4 font-semibold text-slate-900">You’re up to date</p>
              <p className="mt-1 text-xs text-slate-500">New operational updates will appear here.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function formatRelative(value: string) {
  const elapsed = Date.now() - Date.parse(value);
  const hours = Math.max(0, Math.floor(elapsed / 3_600_000));
  if (hours < 1) return 'Just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
